import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import type { WorkOrderPriceItem } from 'gql/graphql';
import {
    CostGroupEnum,
    ActKc2StatusEnum,
    ContractStatusEnum,
    WorkOrderPriceTypeEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { Text } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import { ActKc2CommentEditModal } from './ActKc2CommentEditModal';

interface IActCommentRenderer extends ICellRendererParams<WorkOrderPriceItem> {
    actId: number;
    actNumber: number | undefined;
    actDate: string | undefined;
    seasonId: number;
    workOrderStatus: WorkOrderStatusEnum;
    contractStatus: ContractStatusEnum;
    isDisabledByProjectStatus: boolean;
    onChange: () => void;
    useAgCellRowDataRefresh: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams: any;
    skzsPanelRefresh: () => void;
}

export const ActKC2CommentCellRenderer = (props: IActCommentRenderer) => {
    const {
        data,
        seasonId,
        actId,
        workOrderStatus,
        isDisabledByProjectStatus,
        contractStatus,
        actNumber,
        actDate,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        skzsPanelRefresh,
        api,
    } = props;

    const { type, isExcludedPrice, seasonValues } = data ?? {};

    const currentSeasonData = seasonValues?.find((season) => season.seasonId === seasonId);

    const currentAct = currentSeasonData?.actValues?.find((act) => act.actId === actId);

    const { reloadHandler } = useAgCellRowDataRefresh({
        api,
        requestParams: {
            ...useAgCellRowDataRefreshParams,
            ...(currentAct?.actCostGroup === CostGroupEnum.Others ? { isOtherItem: true } : { isSMRItem: true }),
        },
    });

    const onSuccess = useCallback(
        (reloadRowsIds?: number[] | null) => {
            reloadHandler(reloadRowsIds);
            skzsPanelRefresh();
        },
        [reloadHandler, skzsPanelRefresh],
    );

    if (!data || type === WorkOrderPriceTypeEnum.OtherGroup || type === WorkOrderPriceTypeEnum.RootObject) {
        return '';
    }

    const isDisabledByWorkOrderStatus =
        workOrderStatus === WorkOrderStatusEnum.Delayed || workOrderStatus === WorkOrderStatusEnum.Completed;
    const isDisabledByWorkContractStatus = contractStatus && contractStatus !== ContractStatusEnum.InImplementation;
    const isDisabledByActStatus =
        currentAct?.actStatus !== ActKc2StatusEnum.OnCheck && currentAct?.actStatus !== ActKc2StatusEnum.InProgress;

    const otherCommentDisabled =
        currentAct?.actStatus === ActKc2StatusEnum.OnCheck ? !currentAct?.actCostAccepted : !currentAct?.actCostPlan;
    const cmrCommentDisabled =
        currentAct?.actStatus === ActKc2StatusEnum.OnCheck
            ? !currentAct?.actVolumeAccepted
            : !currentAct?.actVolumePlan;
    const commentDisabledByValue =
        data?.type === WorkOrderPriceTypeEnum.Other ? otherCommentDisabled : cmrCommentDisabled;

    const isNotEditable = Boolean(
        isDisabledByProjectStatus ||
            isDisabledByWorkOrderStatus ||
            isDisabledByWorkContractStatus ||
            isDisabledByActStatus ||
            isExcludedPrice,
    );

    const value = currentAct?.comment ?? '—';

    return currentAct ? (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: 'auto',
                overflowX: 'hidden',
            }}
        >
            {!isNotEditable && (
                <ActKc2CommentEditModal
                    disabled={commentDisabledByValue}
                    comment={currentAct?.comment ?? ''}
                    actNumber={actNumber}
                    actDate={actDate}
                    actId={currentAct?.actId}
                    actCostGroup={currentAct?.actCostGroup}
                    priceId={data?.itemId}
                    onSuccess={onSuccess}
                />
            )}

            <Text view={currentAct?.comment ? 'primary' : 'secondary'} size="s" display="inlineBlock" as="span">
                {value}
            </Text>
        </div>
    ) : null;
};

ActKC2CommentCellRenderer.displayName = 'ActKC2CommentCellRenderer';
